import { Call, Device } from '@twilio/voice-sdk';
import { notification } from 'antd';
import i18n from 'i18next';
import { TwilioCall } from '@/types/app.types';
import { deviceOptions } from '@modules/phone/constants/device.constants';
import { LocalStorageKeys } from '@shared/constants';
import { ActiveCall, ActiveDevice, CallDirection, CallProvider, CallStatus, OutgoingCallParams } from '../types';
import { registerProvider } from '../provider-registry';
import { isMemberCall } from './helpers';

const DEFAULT_DEVICE_ID = 'default';
const INPUT_LOSS_DEBOUNCE_MS = 1000;

function asTwilioCall(call: ActiveCall) {
  return call.providerCall as TwilioCall;
}

function asTwilioDevice(device: ActiveDevice) {
  return device.providerDevice as Device;
}

function toTwilioConnectParams(params: OutgoingCallParams): Record<string, string> {
  if (params.kind === 'number') return { From: params.from, To: params.to };
  return { strategyExternalId: params.strategyExternalId, strategyReferenceId: params.strategyReferenceId };
}

async function applyInputDevice(device: Device, deviceId: string): Promise<boolean> {
  try {
    await device.audio?.setInputDevice(deviceId);
    return true;
  } catch {
    if (deviceId === DEFAULT_DEVICE_ID) return false;
    return applyInputDevice(device, DEFAULT_DEVICE_ID);
  }
}

async function applyOutputDevice(device: Device, deviceId: string) {
  await device.audio?.speakerDevices.set(deviceId).catch(() => undefined);
  await device.audio?.ringtoneDevices.set(deviceId).catch(() => undefined);
}

function watchForLostInputDevice(device: Device) {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  device.audio?.on('deviceChange', () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const audio = device.audio;
      const currentInput = audio?.inputDevice;
      if (!currentInput || audio?.availableInputDevices.has(currentInput.deviceId)) return;

      applyInputDevice(device, DEFAULT_DEVICE_ID).then((recovered) => {
        notification.warning({
          message: i18n.t(
            recovered
              ? 'microphone_disconnected_switched_to_default_device'
              : 'microphone_disconnected_no_device_available'
          ),
        });
      });
    }, INPUT_LOSS_DEBOUNCE_MS);
  });
}

export const twilioProvider: CallProvider = {
  createDevice: (token) => {
    const device = new Device(token, deviceOptions);
    device.register().catch((error) => console.error('Twilio device registration failed', error));

    const savedInputDeviceId = localStorage.getItem(LocalStorageKeys.audioInputDeviceId);
    const savedOutputDeviceId = localStorage.getItem(LocalStorageKeys.audioOutputDeviceId);
    if (savedInputDeviceId) applyInputDevice(device, savedInputDeviceId);
    if (savedOutputDeviceId) applyOutputDevice(device, savedOutputDeviceId);
    watchForLostInputDevice(device);

    return { provider: 'twilio', providerDevice: device };
  },
  updateDeviceToken: (device, token) => asTwilioDevice(device).updateToken(token),
  getDeviceToken: (device) => asTwilioDevice(device).token ?? '',
  isDeviceBusy: (device) => asTwilioDevice(device).isBusy,
  getActiveCallCount: (device) => asTwilioDevice(device).calls.length,
  disconnectAllCalls: (device) => asTwilioDevice(device).disconnectAll(),
  connect: async (device, params) => {
    const call = await asTwilioDevice(device).connect({ params: toTwilioConnectParams(params) });
    return { provider: 'twilio', providerCall: call };
  },

  accept: (call) => asTwilioCall(call).accept(),
  reject: (call) => asTwilioCall(call).reject(),
  ignore: (call) => asTwilioCall(call).ignore(),
  disconnect: (call) => asTwilioCall(call).disconnect(),
  mute: (call, shouldMute) => asTwilioCall(call).mute(shouldMute),
  sendDigits: (call, digits) => asTwilioCall(call).sendDigits(digits),
  getCallId: (call) => asTwilioCall(call).parameters.CallSid,
  getDirection: (call): CallDirection =>
    asTwilioCall(call).direction === Call.CallDirection.Incoming ? 'incoming' : 'outgoing',
  getStatus: (call) => asTwilioCall(call).status() as CallStatus,
  isAnswered: (call) => !!asTwilioCall(call)._isAnswered,
  isMemberCall: (call) => isMemberCall(asTwilioCall(call)),
  getCustomParam: (call, key) => asTwilioCall(call).customParameters.get(key),

  setInputDevice: async (device, deviceId) => {
    const twilioDevice = asTwilioDevice(device);
    try {
      await twilioDevice.audio?.setInputDevice(deviceId);
      return true;
    } catch {
      await applyInputDevice(twilioDevice, DEFAULT_DEVICE_ID);
      return false;
    }
  },
  setOutputDevice: (device, deviceId) => applyOutputDevice(asTwilioDevice(device), deviceId),
  releaseInputDevice: (device) => asTwilioDevice(device).audio?.unsetInputDevice() ?? Promise.resolve(),
};

registerProvider('twilio', twilioProvider);
